import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Paper, Grid, Button, Typography, Box } from "@mui/material";
import RequisitionRequest_Gray from "../../assets/images/icons/RequisitionRequest_Gray.svg";
import OnboardingTemplateInformation_Gray from "../../assets/images/icons/OnboardingTemplateInformation_Gray.svg";
import CenterPoint_Gray from "../../assets/images/icons/CenterPoint_Gray.svg";
import LearnPoint_Gray from "../../assets/images/icons/LearnPoint_Gray.svg";
import Core_Ratio from "../../assets/images/icons/Core_Ratio.svg";
import LastestUpdate from "../../assets/images/icons/LastestUpdate.svg";
import ProcessGuide from "../../assets/images/icons/ProcessGuide.svg";
import QuestionMark from "../../assets/images/icons/QuestionMark.svg";
import classes from "./Welcome.module.css";

import { styled } from "@mui/material/styles";

import applicationUsageLog from "../../utils/applicationUsagesLog";
import {
  CENTERPOINT,
  HOME,
  LEARNPOINT_HOME,
  OBT_INFO_MANAGEMENT,
  MOSAIC,
  PROCESS_GUIDE,
  CORERATIO,
} from "../../navigation/CONSTANTS";
import SnackBar from "components/SnackBar/SnackBar";
import { getOracleId } from "services/centerPointUserServices";
import BackdropComponent from "components/Spinner/Spinner";

import Header from "components/Common/Header/Header";
import Sidebar from "components/Common/SideBar/SideBar";
import landingRightGraphic from "../../../src/assets/images/Landing Page Main Image.svg";
import welcomeNote from "../../../src/assets/images/WelcomeNotes.svg";
import { useAuth } from "context/AuthContext";

function Welcome() {
  const { hasPermission } = useAuth();

  const index = 0;
  const [loader, setLoader] = useState(false);
  const [enableCenterPoint, setEnableCenterPoint] = useState(false);
  const [enableCoreRatio, setEnableCoreRatio] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = () => {
      if (hasPermission("CP_Group")) setEnableCenterPoint(true);
      if (hasPermission("CoreRatio_Group")) setEnableCoreRatio(true);

      applicationUsageLog(
        localStorage.getItem("User"),
        localStorage.getItem("EmailId"),
        "ASTT",
        "Welcome",
      );
    };

    fetchData();
  }, [hasPermission]);

  useEffect(() => {
    let cancelled = false;

    const fetchAndSetOracleId = async () => {
      if (!cancelled) {
        setLoader(true);
      }

      try {
        if (!localStorage?.getItem("OracleId")) {
          const response = await getOracleId();

          if (cancelled) return;

          if (response?.data?.message === "Success") {
            if (!cancelled) {
              localStorage.setItem("OracleId", response.data.data[0]?.OracleId);
            }
          } else {
            if (!cancelled) {
              errorHandler(true, "Oracle Id could not be fetched !", "error");
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          errorHandler(
            true,
            "Unable to login, Authentication error occured.",
            "error",
          );
          console.log("error while fetching oracleId", error);
        }
      } finally {
        if (!cancelled) {
          setLoader(false);
        }
      }
    };

    fetchAndSetOracleId();

    return () => {
      cancelled = true;
    };
  }, []);

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const IconWrapper = styled("div")({
    height: "25%",
  });

  const ContentWrapper = styled("div")({
    height: "30%",
    paddingTop: "20px",
  });

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const paperStyles = {
    height: 300,
    width: 200,
    backgroundColor: "#fff",
    my: 2,
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };

  const buttonStyles = {
    mt: 10,
    borderRadius: 4,
    width: "90%",
    background: "#06a5ff",
  };

  const buttonTemplate = (
    key,
    icon,
    buttonText,
    url,
    enabled,
    headingText,
    subText,
  ) => (
    <Paper sx={paperStyles} key={key}>
      <IconWrapper>{icon}</IconWrapper>
      <ContentWrapper>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            fontFamily: "Urbanist, sans-serif;",
          }}
        >
          {headingText}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            paddingLeft: 2,
            paddingRight: 2,
            fontFamily: "Urbanist, sans-serif;",
          }}
        >
          {subText}
        </Typography>
      </ContentWrapper>

      <Button
        variant="contained"
        sx={buttonStyles}
        onClick={() => {
          navigate(url);
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            headingText === "ASTT Home" ? "ASTT" : headingText, // Application
            headingText !== "ASTT Home" ? headingText + " Home" : headingText, // Module
          );
        }}
        disabled={!enabled}
      >
        <Typography
          variant="button"
          className={classes.mainFont}
          sx={{
            fontWeight: "bold",
          }}
        >
          {buttonText}
        </Typography>
      </Button>
    </Paper>
  );

  const buttons = [
    {
      headingText: "ASTT Home",
      subText:
        "TTEC proprietary tool designed to effectively manage the detailed status of new associates as they move from hire to production.",
      icon: <img alt="ASTT Home" src={RequisitionRequest_Gray} width={100} />,
      text: "View Portal",
      url: HOME,
      enabled: true,
    },
    {
      headingText: "Onboarding Information",
      subText:
        "Hub for various onboarding systems, such as the Contact Roster, AskNow, Milestone Configuration, and ID Management tracking.",
      icon: (
        <img
          alt="Onboarding Information"
          src={OnboardingTemplateInformation_Gray}
          width={100}
        />
      ),
      text: "View Portal",
      url: OBT_INFO_MANAGEMENT,
      enabled: true,
    },
    {
      headingText: "CenterPoint",
      subText: "A repository of TTEC and Client credentials for all Employees.",
      icon: <img alt="Center Point" src={CenterPoint_Gray} width={100} />,
      text: "View Portal",
      url: CENTERPOINT,
      enabled: enableCenterPoint,
    },
    {
      headingText: "LearnPoint",
      subText: "Manages Trainees’ Journey during Training until Production.",
      icon: <img alt="LearnPoint" src={LearnPoint_Gray} width={100} />,
      text: "View Portal",
      url: LEARNPOINT_HOME,
      enabled: true,
    },
    {
      headingText: "Core Ratio",
      subText:
        "Hub for managing Country, Job Code and Project mapping for Core Ratio purposes.",
      icon: <img alt="CoreRatio" src={Core_Ratio} width={100} />,
      text: "View Portal",
      url: CORERATIO,
      enabled: enableCoreRatio,
    },
  ];

  const resourcePaperStyles = {
    height: 210,
    width: 200,
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText("asttdevelopment@ttec.com");
    setSnack({
      open: true,
      message: "Copied!",
      severity: "success",
    });
  };

  const resourceTemplate = (key, icon, url, buttonText) => (
    <Paper sx={resourcePaperStyles} key={key}>
      {url === "ISSUE_REQUEST" ? (
        <div className={classes.resourceQuestion}> {icon}</div>
      ) : (
        <div className={classes.resourceIcon}> {icon}</div>
      )}

      {url === "ISSUE_REQUEST" ? (
        <Box sx={{ paddingTop: "100px", color: "#3768E8" }}>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontFamily: "Urbanist, sans-serif;",
              color: "#3768E8",
              fontSize: "1.1rem",
              fontWeight: 600,
            }}
          >
            {buttonText}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              paddingTop: "5px",
              textAlign: "center",
              fontFamily: "Urbanist, sans-serif;",
            }}
            onClick={copyToClipboard}
          >
            <span className={classes.supportLink}>
              asttdevelopment@ttec.com
            </span>
          </Typography>
        </Box>
      ) : (
        <Box sx={{ paddingTop: "125px", color: "#3F5765" }}>
          <a
            className={classes.resourceLink}
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {buttonText}
          </a>
        </Box>
      )}
    </Paper>
  );

  const resourceInfo = [
    {
      icon: <img alt="Latest Updates" src={LastestUpdate} />,
      url: MOSAIC,
      text: "Latest Updates",
    },
    {
      icon: <img alt="User Process Guide" src={ProcessGuide} />,
      url: PROCESS_GUIDE,
      text: "User Process Guide",
    },
    {
      icon: <img alt="Issue Request" src={QuestionMark} />,
      url: "ISSUE_REQUEST",
      text: "Issue & Request Contacts",
    },
  ];
  return (
    <>
      {loader ? <BackdropComponent /> : null}
      <Grid container spacing={2} className={classes.welcomeBody}>
        <Grid item xs={0.75} className={classes.grid}>
          <Sidebar index={index} />
        </Grid>
        <Grid item xs={11.25} className={classes.grid}>
          <Header
            title="Agent State Tracker"
            logoURL="/welcome"
            version="new"
          />
          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid item xs={6}>
              <div>
                <img alt="Welcome" src={welcomeNote} />
              </div>
              <span className={classes.welcomeNotes}>
                Your single source for timely, consistent and accurate
                information from hire to production.
              </span>
            </Grid>
            <Grid item xs={6}>
              <img
                className={classes.welcomeImage}
                src={landingRightGraphic}
                alt="TTEC"
              />
            </Grid>
            <Grid item xs={12}>
              <span className={classes.portalText}>Select A Portal</span>
              <Grid className={classes.cardContainer}>
                {buttons.map((button, index) =>
                  buttonTemplate(
                    index,
                    button.icon,
                    button.text,
                    button.url,
                    button.enabled,
                    button.headingText,
                    button.subText,
                  ),
                )}
              </Grid>
            </Grid>
            <Grid className={classes.titleResource} item xs={12}>
              <span>Resources</span>
            </Grid>
            <Grid item xs={12}>
              <Grid
                container
                justifyContent="center"
                sx={{
                  maxWidth: "100%",
                  height: "100%",
                }}
              >
                {resourceInfo.map((button, index) =>
                  resourceTemplate(index, button.icon, button.url, button.text),
                )}
              </Grid>
            </Grid>
            <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Welcome;
